import React, { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';

// API URLs
const API_URL = 'https://bq.dev.rhombusquest.com/bq/api/rooms/alldetails';
const AVAILABILITY_API_URL = 'https://bq.dev.rhombusquest.com/bq/api/availability/';

// RoomRow Component
const RoomRow = ({ room }) => {
  const formattedStatus = room.status.charAt(0).toUpperCase() + room.status.slice(1);
  return (
    <tr className="border-b">
      <td className="py-3 px-6 text-left">{room.id}</td>
      <td className="py-3 px-6 text-left">{room.room_no}</td>
      <td className="py-3 px-6 text-left">{formattedStatus}</td>
      <td className="py-3 px-6 text-left">₹{room.room_price}</td>
      <td className="py-3 px-6 text-left">{room.room_type}</td>
    </tr>
  );
};

// Main Component
const RoomManagement = () => {
  const [roomData, setRoomData] = useState({});
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roomStatus, setRoomStatus] = useState('all');
  const [selectedRoomType, setSelectedRoomType] = useState('all');
  // const [arrivalDate, setArrivalDate] = useState("");
  // const [departureDate, setDepartureDate] = useState("");
  const [onlyAvailableRooms, setOnlyAvailableRooms] = useState(false);

  // Fetch room details and available room types
  useEffect(() => {
    const fetchRoomDetails = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();

        // Flatten the data and categorize rooms by their type
        const categorizedRooms = {};

        for (const category in data.rooms) {
          // Ensure each category is initialized as an array
          categorizedRooms[category] = [];

          for (const status in data.rooms[category]) {
            data.rooms[category][status].forEach((room) => {
              categorizedRooms[category].push({
                ...room,
                room_type: category,
              });
            });
          }
        }

        setRoomData(categorizedRooms);
      } catch (error) {
        console.error('Error fetching room details:', error);
        setRoomData({}); // Set to empty object if there's an error
      }
    };

    const fetchAvailableRoomTypes = async () => {
      try {
        const response = await fetch(AVAILABILITY_API_URL);
        const data = await response.json();
        setAvailableRoomTypes(data.available_room_types);
      } catch (error) {
        console.error('Error fetching available room types:', error);
        setAvailableRoomTypes([]);
      }
    };

    fetchRoomDetails();
    fetchAvailableRoomTypes();
  }, []);

  // Filter rooms based on search criteria
  const filteredRooms = Object.keys(roomData).map((roomType) => {
    const rooms = roomData[roomType].filter((room) => {
      const matchesSearch =
        room.room_price.toString().includes(searchTerm) ||
        room.room_no.toString().includes(searchTerm);
      const matchesStatus = roomStatus === 'all' || room.status.toLowerCase() === roomStatus;
      const matchesRoomType = selectedRoomType === 'all' || room.room_type === selectedRoomType;
      const matchesAvailability = !onlyAvailableRooms || room.status.toLowerCase() === 'available';

      return matchesSearch && matchesStatus && matchesRoomType && matchesAvailability;
    });

    return { roomType, rooms };
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoomStatus('all');
    // setArrivalDate("");
    // setDepartureDate("");
    setSelectedRoomType('all');
    setOnlyAvailableRooms(false);
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold  text-center">Room Management</h1>
      <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-500">
        Efficient room management ensures seamless booking, maintenance, and comfort, providing
        guests with an exceptional experience throughout their stay.
      </h1>
      <hr className="border border-gray-500 w-1/2 mx-auto mb-7" />

      {/* Filters Section */}
      <div className="flex  justify-between items-center gap-2 mb-8">
        <div className="w-full sm:w-1/3 mb-4 sm:mb-0 relative">
          <input
            type="text"
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg"
            placeholder="Search by Room Number or Rate"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black"
          />
        </div>

        <div className="w-full sm:w-1/4 mb-4 sm:mb-0">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={roomStatus}
            onChange={(e) => setRoomStatus(e.target.value)}
          >
            <option value="all">Rooms Status</option>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="occupied">Occupied</option>
            <option value="unavailable">Unavailable</option>
          </select>
        </div>

        <div className="w-full sm:w-1/4 mb-4 sm:mb-0">
          <select
            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            value={selectedRoomType}
            onChange={(e) => setSelectedRoomType(e.target.value)}
          >
            <option value="all">All Room Types</option>
            {Object.keys(availableRoomTypes).map((roomType) => (
              <option key={roomType} value={roomType}>
                {roomType}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full sm:w-1/4 mb-4 sm:mb-0">
          <button
            onClick={() => setOnlyAvailableRooms(!onlyAvailableRooms)}
            className={`w-full py-2 px-4 rounded-lg text-white ${
              onlyAvailableRooms ? 'bg-cyan-500 hover:text-black' : 'bg-cyan-500 hover:text-black'
            }`}
          >
            {onlyAvailableRooms ? 'All Rooms' : 'Available Rooms'}
          </button>
        </div>

        <div className="w-full sm:w-1/4">
          <button
            onClick={handleClearFilters}
            className="w-full sm:w-auto px-4 py-2 bg-red-500 text-white rounded-lg hover:text-black"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Room Table */}
      <div className="space-y-8">
        {filteredRooms.map(({ roomType, rooms }) => {
          if (rooms.length === 0) return null;

          return (
            <div key={roomType}>
              <h2 className="bg-gray-100 text-black font-bold text-left py-3 px-6 rounded-t-lg">
                {roomType} Rooms
              </h2>

              <div className="overflow-x-auto">
                <table className="min-w-full table-auto bg-white border border-gray-300 rounded-lg shadow-lg">
                  <thead className="bg-cyan-500 text-white">
                    <tr>
                      <th className="py-3 px-6 text-left">Room ID</th>
                      <th className="py-3 px-6 text-left">Room No</th>
                      <th className="py-3 px-6 text-left">Status</th>
                      <th className="py-3 px-6 text-left">Price</th>
                      <th className="py-3 px-6 text-left">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room) => (
                      <RoomRow key={room.id} room={room} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RoomManagement;
